<template>
  <RevTable
    :collection="opportunities"
    :columns="formattedColumns"
    :sort-options
    transparent-header
    @update:sort-options="handleSortOptionChanged"
  >
    <template #body-grade="{ item }">
      <RevTag :label="item.grade" variant="secondary" />
    </template>
    <template #body-marketCode="{ item }">
      <Flag :country-code="item.marketCode" size="l" />
    </template>
    <template #body-priceToWinBackboxEstimation="{ item }">
      {{ i18n.price(item.priceToWinBackboxEstimation) }}
    </template>
    <template #body-margin="{ item }">
      <div class="text-static-success-hi">
        {{ getMarginFromGrade(item) }}

        <RevTooltip v-if="showInfoTooltip(item)" position="top">
          <template #trigger="{ hide, show }">
            <IconInfo
              class="ml-4 opacity-[0.8]"
              data-test="info-icon"
              @focusin="show"
              @focusout="hide"
              @mouseenter="show"
              @mouseleave="hide"
            />
          </template>
          {{ i18n(translations.marginInfoTooltip) }}
        </RevTooltip>
      </div>
    </template>
    <template #body-averageSellingPrice="{ item }">
      {{ i18n.price(item.averageSellingPrice) }}
    </template>
    <template #body-competitionLevel="{ item }">
      <RevTag
        :label="getLabelFromCompetitionLevel(item.competitionLevel)"
        :variant="getVariantFromCompetitionLevel(item.competitionLevel)"
      />
    </template>
    <template #body-action="{ item }">
      <RevButton variant="primary" @click="handleCreateListing(item.productId)">
        {{ getActionLabel(item.action) }}
      </RevButton>
    </template>
  </RevTable>
</template>

<script setup lang="ts">
import { navigateTo } from '#imports'
import { computed } from 'vue'

import Flag from '@backmarket/nuxt-layer-base/components/Flag/Flag.vue'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { RevButton } from '@ds/components/Button'
import { RevTable, type SortOptions } from '@ds/components/Table'
import { RevTag } from '@ds/components/Tag'
import { RevTooltip } from '@ds/components/Tooltip'
import { IconInfo } from '@ds/icons/IconInfo'

import {
  ACTIONS,
  COMPETITION_LEVELS,
  ORDER_BY,
  QUOTES_LISTING_GRADES,
  type SORT_ORDER_ENUM,
  type SourcingOpportunity,
} from '~/api/quotes/types'
import { ROUTE_NAMES } from '~/constants/routes'

import translations from './OpportunitiesDataTable.translations'

const i18n = useI18n()
const { trackClick } = useTracking()

defineProps<{
  opportunities: SourcingOpportunity[]
}>()

const sortOrderInput = defineModel<SORT_ORDER_ENUM | undefined>(
  'sortOrderInput',
  {
    required: true,
  },
)
const sortByInput = defineModel<string>('sortByInput', { required: true })
const DEFAULT_NON_FUNCTIONAL_MARGIN_AMOUNT = '150.00' as const

function getOrderEnumByKey(key: string) {
  switch (key) {
    case 'profitPotentialRank':
      return ORDER_BY.PROFIT_POTENTIAL
    case 'priceToWinBackboxEstimation':
      return ORDER_BY.PRICE_TO_WIN
    case 'margin':
      return ORDER_BY.MARGIN
    case 'averageSellingPrice':
      return ORDER_BY.SELLING_PRICE
    case 'ordersVolume':
      return ORDER_BY.ORDER_VOLUME
    default:
      return key
  }
}

function getOrderKeyByEnum(enumValue: string) {
  switch (enumValue) {
    case ORDER_BY.PROFIT_POTENTIAL:
      return 'profitPotentialRank'
    case ORDER_BY.PRICE_TO_WIN:
      return 'priceToWinBackboxEstimation'
    case ORDER_BY.MARGIN:
      return 'margin'
    case ORDER_BY.SELLING_PRICE:
      return 'averageSellingPrice'
    case ORDER_BY.ORDER_VOLUME:
      return 'ordersVolume'
    default:
      return enumValue
  }
}

const sortOptions = computed<SortOptions>(() => ({
  sortBy: getOrderKeyByEnum(sortByInput.value),
  sortOrder:
    sortOrderInput.value === undefined ? 'UNDEFINED' : sortOrderInput.value,
}))

const formattedColumns = [
  {
    key: 'profitPotentialRank',
    label: i18n(translations.profitPotentialRank),
    isSortable: true,
    description: i18n(translations.profitPotentialRankDescription),
  },
  { key: 'productTitle', label: i18n(translations.productTitle) },
  { key: 'grade', label: i18n(translations.grade) },
  { key: 'marketCode', label: i18n(translations.marketCode) },
  {
    key: 'priceToWinBackboxEstimation',
    label: i18n(translations.priceToWinBackboxEstimation),
    isSortable: true,
  },
  {
    key: 'margin',
    label: i18n(translations.margin),
    isSortable: true,
    highlighted: true,
    description: i18n(translations.marginDescription),
  },
  {
    key: 'averageSellingPrice',
    label: i18n(translations.averageSellingPrice),
    isSortable: true,
    description: i18n(translations.averageSellingPriceDescription),
  },
  {
    key: 'ordersVolume',
    label: i18n(translations.ordersVolume),
    isSortable: true,
    description: i18n(translations.ordersVolumeDescription),
  },
  {
    key: 'competitionLevel',
    label: i18n(translations.competitionLevel),
    description: i18n(translations.competitionLevelDescription),
  },
  {
    key: 'action',
    label: '',
  },
]

function handleSortOptionChanged(options: SortOptions) {
  sortOrderInput.value =
    options.sortOrder === 'UNDEFINED' ? undefined : options.sortOrder
  sortByInput.value = getOrderEnumByKey(options.sortBy)

  trackClick({
    name: 'sort_opportunities',
    zone: 'opportunities_filters',
    value: options.sortBy,
  })
}

function handleCreateListing(productId: string) {
  navigateTo({
    name: ROUTE_NAMES.BUYBACK.CREATE_QUOTATION,
    params: { productId },
  })

  trackClick({
    name: 'create_listing',
    zone: 'opportunities_filters',
    value: productId,
  })
}

function getActionLabel(action: string) {
  switch (action) {
    case ACTIONS.CREATE_LISTING:
      return i18n(translations.createListing)
    case ACTIONS.TURN_ONLINE:
      return i18n(translations.turnOnline)
    case ACTIONS.UPDATE_PRICE:
    default:
      return i18n(translations.updatePrice)
  }
}

function getLabelFromCompetitionLevel(competitionLevel: string) {
  switch (competitionLevel) {
    case COMPETITION_LEVELS.HIGH:
      return i18n(translations.high)
    case COMPETITION_LEVELS.MEDIUM:
      return i18n(translations.medium)
    case COMPETITION_LEVELS.LOW:
      return i18n(translations.low)
    case COMPETITION_LEVELS.VERY_LOW:
    default:
      return i18n(translations.veryLow)
  }
}

function getVariantFromCompetitionLevel(competitionLevel: string) {
  switch (competitionLevel) {
    case COMPETITION_LEVELS.HIGH:
      return 'danger'
    case COMPETITION_LEVELS.MEDIUM:
      return 'warning'
    case COMPETITION_LEVELS.LOW:
      return 'success'
    case COMPETITION_LEVELS.VERY_LOW:
    default:
      return 'info'
  }
}

function getMarginFromGrade(item: SourcingOpportunity) {
  switch (item.grade) {
    case QUOTES_LISTING_GRADES.NOT_FUNCTIONAL_CRACKED:
    case QUOTES_LISTING_GRADES.NOT_FUNCTIONAL_USED:
      return `> ${i18n.price({
        amount: DEFAULT_NON_FUNCTIONAL_MARGIN_AMOUNT,
        currency: item.margin.currency,
      })}`
    default:
      return i18n.price(item.margin)
  }
}

function showInfoTooltip(item: SourcingOpportunity) {
  switch (item.grade) {
    case QUOTES_LISTING_GRADES.NOT_FUNCTIONAL_CRACKED:
    case QUOTES_LISTING_GRADES.NOT_FUNCTIONAL_USED:
      return true
    default:
      return false
  }
}
</script>
